import { MouseEvent, useCallback, useEffect, useRef } from "react"
import { Link as RouterLink } from "react-router-dom"
import CallToAction from "../components/CallToAction"
import Footer from "../components/Footer"
import LandingStory from "../landing/LandingStory"
import "../landing/landing.css"

/* The 3D engine (three.js) is loaded on demand so it stays out of the main bundle for the rest of the app */
type MountedHome = Awaited<ReturnType<typeof import("../landing/home").mountHome>>

const sectorCards = [
  {
    label: "Institutional Investors",
    tagline:
      "Pinpoint high-yield retrofit opportunities across large portfolios with clear, quantified ROI.",
    to: "/institutional-investors",
    icon: "M4 20l6-6 4 4 6-8"
  },
  {
    label: "Social Landlords",
    tagline:
      "Assess your entire portfolio, deploy your budget efficiently and unlock returns through smart tariffs.",
    to: "/social-landlords",
    icon: "M4 11l8-7 8 7v9H4z M10 20v-5h4v5"
  },
  {
    label: "Energy Companies",
    tagline:
      "Identify the best homes for solar and heat pump deployment at scale.",
    to: "/energy-companies",
    icon: "M13 3L5 14h6l-1 7 8-11h-6z"
  }
]

const supporterLogos = [
  { src: "/images/carbon13-logo.png", alt: "Carbon13" },
  { src: "/images/scottish-edge-logo.png", alt: "Scottish EDGE" },
  { src: "/images/geovation-logo.png", alt: "Geovation by Ordnance Survey" },
  { src: "/images/os-logo.png", alt: "Ordnance Survey" },
  { src: "/images/google-cloud-logo.png", alt: "Google Cloud for startups" },
  {
    src: "/images/sage-impact-logo.png",
    alt: "Sage Impact Entrepreneurship Programme 2026"
  }
]

const valueCards = [
  {
    title: "Physics first",
    body: "Heat loss modelled element by element, and overheating risk room by room, so every recommendation starts from how the building actually performs."
  },
  {
    title: "Every upgrade path",
    body: "Heat pumps sized to the house, solar and batteries fitted to each roof, and shading or cooling where rooms overheat, with funding such as the Boiler Upgrade Scheme flagged automatically."
  },
  {
    title: "Returns you can underwrite",
    body: "Upgrade cost, bill savings with smart tariffs, ROI, payback and the uplift in home value for each path. Not an energy rating: a decision-ready investment case."
  },
  {
    title: "One home or 10,000",
    body: "Screen a single acquisition or triage an entire portfolio, with every home ranked by return and the same per-property precision at no marginal cost."
  }
]

const STAGE_LABEL =
  "3D model of a UK home on a white plinth, being analysed for retrofit"

const LandingPage: React.FC = () => {
  const storyRef = useRef<HTMLElement>(null)
  const homeRef = useRef<MountedHome | null>(null)

  /* Mount the 3D story. A fresh canvas per mount, because destroy() releases its WebGL context
     (React StrictMode mounts effects twice in development). */
  useEffect(() => {
    const story = storyRef.current
    const viewport = story?.querySelector(".viewport")
    if (!story || !viewport) return

    const canvas = document.createElement("canvas")
    canvas.setAttribute("role", "img")
    canvas.setAttribute("aria-label", STAGE_LABEL)
    viewport.prepend(canvas)

    let cancelled = false
    import("../landing/home")
      .then(({ mountHome }) => mountHome({ canvas }))
      .then((home) => {
        if (cancelled) home.destroy()
        else homeRef.current = home
      })
      .catch((error) => {
        // No WebGL (or the model failed to build): fall back to the chapters as a plain page
        console.warn("Landing page: 3D story unavailable", error)
        if (!cancelled) story.classList.add("no-3d")
      })

    return () => {
      cancelled = true
      homeRef.current?.destroy()
      homeRef.current = null
      canvas.remove()
    }
  }, [])

  const handleHowItWorks = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!homeRef.current) return // no 3D story: let the #c1 anchor jump there
      event.preventDefault()
      homeRef.current.api.go(1)
    },
    []
  )

  return (
    <>
      <div className="gf-landing">
        <a className="skip" href="#early-access">
          Skip to sign-up form
        </a>

        <LandingStory ref={storyRef} onHowItWorks={handleHowItWorks} />

        {/* Built for your sector */}
        <section className="sx sectors" id="sectors">
          <p className="sx-kicker">Built for your sector</p>
          <h2 className="sx-title">One engine. Three kinds of portfolio.</h2>
          <div className="sector-grid">
            {sectorCards.map((card) => (
              <RouterLink
                key={card.to}
                className="sector"
                to={card.to}
                aria-label={`Learn more about GreenFlip for ${card.label}`}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={card.icon} />
                </svg>
                <h3>{card.label}</h3>
                <p>{card.tagline}</p>
                <span className="more">Learn more →</span>
              </RouterLink>
            ))}
          </div>
        </section>

        {/* Supported by: the row is doubled so the marquee loops seamlessly */}
        <section className="sx logos" aria-label="Supported by">
          <p className="sx-kicker">Supported by</p>
          <div className="logo-track">
            <div className="logo-row">
              {[...supporterLogos, ...supporterLogos].map((logo, i) => (
                <img
                  key={`${logo.src}-${i}`}
                  src={logo.src}
                  alt={i < supporterLogos.length ? logo.alt : ""}
                  aria-hidden={i >= supporterLogos.length || undefined}
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        </section>

        {/* Value proposition: a recap of what the story above walks through */}
        <section className="sx value" id="value">
          <h2 className="sx-title">
            Focus retrofit where it delivers highest value
          </h2>
          <p className="sx-lede">
            From a street address, GreenFlip builds a digital twin of each home,
            models how it loses heat and where it overheats, then costs every
            upgrade path into an investment case you can underwrite.
          </p>
          <div className="value-grid">
            <div className="v big">
              <strong>99%</strong>
              <span>Reduction in assessment time vs physical survey</span>
              <p>
                Building geometry, EPC records and local solar irradiance come
                from public datasets, so a portfolio analysis that takes a
                surveying team weeks runs in minutes, with no site visit.
              </p>
            </div>
            {valueCards.map((card) => (
              <div className="v" key={card.title}>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <CallToAction
        title="Find the retrofits worth funding in your portfolio."
        description="Get early access to see heat loss, overheating risk, upgrade costs and returns for every home you own or manage, starting from a list of addresses."
      />
      <Footer />
    </>
  )
}

export default LandingPage
